import { StyledDivider } from 'modules/productAnalytics/Styled';
import { formatNumberGivenValueType, metricSentenceCase } from 'modules/productAnalytics/utils';
import React from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { Skeleton } from 'ui/skeleton';
import { localize } from 'utils/numberHelpers';
import { kebabCase } from 'utils/stringHelpers';

import { LayoutTemplate } from './LayoutTemplate';
import { MetricBreakdown } from './MetricBreakdown';
import { MetricBreakdownCard } from './MetricBreakdownCard';

import { RETENTION_SECONDARY_NICENAME, RETENTION_UNSUBSCRIBES_NICENAME } from '../constants';
import {
  selectAnalyticsMetricsValueTypes,
  selectCampaignAnalyticsStatsForTimerange,
  selectCampaignMetrics,
  selectIsLoading,
  selectMessageMedium,
} from '../selectors';
import { parseMetricsForBreakdown } from '../utils';
import { MetricDefinitionWrapper } from './MetricDefinitionWrapper';
import {
  LeftCol,
  MetricBreakdownContainer,
  MetricBreakdownHeaderContainer,
  MetricBreakdownHeaderTitle,
  MetricBreakdownHeaderValue,
  MetricBreakdownRowWithTwoEqualColumns,
  MetricList,
  MetricRow,
  RightCol,
  Row,
  SkeletonWrapper,
  StyledListContainer,
  SubmetricRow,
} from './Styled';

export const CampaignAnalyticsRetention = () => {
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const campaignMetrics = useSelector(selectCampaignMetrics);
  const valueTypes = useSelector(selectAnalyticsMetricsValueTypes);

  const unsubscribeSources = campaignMetrics?.['Unsubscribe Sources']?.value || {};
  const isLoading = useSelector(selectIsLoading);
  const messageMedium = useSelector(selectMessageMedium);

  const unsubOverview = parseMetricsForBreakdown(
    RETENTION_UNSUBSCRIBES_NICENAME[messageMedium] || [],
    statsForTimeRange?.results,
  );
  const unsubOverviewTitle = messageMedium === 'Embedded' ? 'Dismiss' : 'Unsubscribes';
  const [unsubOverviewHeader, ...unsubOverviewList] = unsubOverview;

  const secondaryOverview = parseMetricsForBreakdown(
    RETENTION_SECONDARY_NICENAME[messageMedium]?.value || [],
    statsForTimeRange?.results,
  );
  const secondaryOverviewTitle = RETENTION_SECONDARY_NICENAME[messageMedium]?.title || '';
  const [secondaryOverviewHeader = [], ...secondaryOverviewList] = secondaryOverview;
  const { name, value, key } = unsubOverviewHeader?.[0] || {};

  return (
    <LayoutTemplate pageName="Audience retention">
      <Row>
        <LeftCol>
          {unsubOverview.length > 0 && (
            <MetricBreakdownCard title={unsubOverviewTitle}>
              {isLoading ? (
                <SkeletonWrapper height={384} padding={24}>
                  <Skeleton />
                </SkeletonWrapper>
              ) : (
                <MetricBreakdownContainer>
                  <MetricBreakdownHeaderContainer>
                    <MetricBreakdownRowWithTwoEqualColumns>
                      <div>
                        <MetricBreakdownHeaderTitle data-test={`metric-title-${name}`}>
                          <MetricDefinitionWrapper metricName={name}>
                            {name}
                          </MetricDefinitionWrapper>
                        </MetricBreakdownHeaderTitle>
                        <MetricBreakdownHeaderValue data-test={`metric-value-${name}`}>
                          {formatNumberGivenValueType(valueTypes[key], value) || 'N/A'}
                        </MetricBreakdownHeaderValue>
                      </div>
                    </MetricBreakdownRowWithTwoEqualColumns>
                  </MetricBreakdownHeaderContainer>
                  <StyledDivider />
                  <StyledListContainer>
                    {unsubOverviewList.map((metricList, idx) => (
                      <MetricList key={`metric-row-${idx}`}>
                        {metricList.map((metric) => {
                          if (metric.name === 'Total Unsubscribes') {
                            return (
                              <React.Fragment key={metric.name}>
                                <MetricRow>
                                  <MetricDefinitionWrapper metricName={metric.name}>
                                    <span data-test={`metric-title-${kebabCase(metric.name)}`}>
                                      {metricSentenceCase(metric.name)}
                                    </span>
                                  </MetricDefinitionWrapper>
                                  <span data-test={`metric-value-${kebabCase(metric.name)}`}>
                                    {formatNumberGivenValueType(
                                      valueTypes[metric.key],
                                      metric.value,
                                    ) || 'N/A'}
                                  </span>
                                </MetricRow>
                                <SubmetricRow>
                                  {Object.keys(unsubscribeSources).map((unsubKey) => (
                                    <MetricRow key={unsubKey}>
                                      <span>{unsubKey}</span>
                                      <span>{localize(unsubscribeSources?.[unsubKey])}</span>
                                    </MetricRow>
                                  ))}
                                </SubmetricRow>
                              </React.Fragment>
                            );
                          }
                          return (
                            <MetricRow key={metric.name}>
                              <MetricDefinitionWrapper metricName={metric.name}>
                                <span data-test={`metric-title-${kebabCase(metric.name)}`}>
                                  {metricSentenceCase(metric.name)}
                                </span>
                              </MetricDefinitionWrapper>
                              <span data-test={`metric-value-${kebabCase(metric.name)}`}>
                                {formatNumberGivenValueType(valueTypes[metric.key], metric.value) ||
                                  'N/A'}
                              </span>
                            </MetricRow>
                          );
                        })}
                      </MetricList>
                    ))}
                  </StyledListContainer>
                </MetricBreakdownContainer>
              )}
            </MetricBreakdownCard>
          )}
        </LeftCol>
        <RightCol>
          {!!secondaryOverviewTitle && (
            <MetricBreakdownCard title={secondaryOverviewTitle}>
              {isLoading ? (
                <SkeletonWrapper height={384} padding={24}>
                  <Skeleton />
                </SkeletonWrapper>
              ) : (
                <MetricBreakdown
                  metricGrid={secondaryOverviewList}
                  headerMetric={secondaryOverviewHeader[0]}
                />
              )}
            </MetricBreakdownCard>
          )}
        </RightCol>
      </Row>
    </LayoutTemplate>
  );
};

export default withRouter(CampaignAnalyticsRetention);
